import React, { useEffect, useMemo, useRef, useState } from "react";
import { Puzzle, Timer, Trophy, RotateCcw, Play, CheckCircle2, XCircle, Flame, ArrowRight, Layers, Sparkles } from "lucide-react";
import { BUTUNLESIK } from "@/data/k405Notes";
import { EGILIMLER, EGILIM_KATEGORI } from "@/data/dispositions";

const LEVELS = [
  { key: "easy", label: "Rahat", secs: 300, desc: "5 dakika" },
  { key: "normal", label: "Normal", secs: 180, desc: "3 dakika" },
  { key: "hard", label: "Zor", secs: 90, desc: "90 saniye" },
];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Set 1: 20 bütünleşik beceri (KB2)
const KB2_ITEMS = BUTUNLESIK.map(([code, name, def]) => ({
  code,
  name: name.replace(/\s*Becerisi$/i, ""),
  def: def.split(" Alt türleri:")[0],
  cat: "KB2",
  catLabel: "Bütünleşik Beceri",
}));
// Set 2: 21 eğilim (E1/E2/E3) + 3 üst düzey düşünme becerisi (KB3)
const EGILIM_ITEMS = EGILIMLER.map(([code, name, def, cat]) => ({ code, name, def, cat, catLabel: EGILIM_KATEGORI[cat] }));

export const MATCH_SETS = [
  { key: "kb2", label: "Bütünleşik Beceriler", sub: "KB2.1 – KB2.20 · 20 eşleştirme · 5'erli 4 tur", items: KB2_ITEMS, perRound: 5, icon: Layers },
  { key: "egilim", label: "Eğilimler + Üst Düzey Beceriler", sub: "E1 / E2 / E3 eğilimleri ve KB3 · 24 eşleştirme · 6'şarlı 4 tur", items: EGILIM_ITEMS, perRound: 6, icon: Sparkles },
];

const EMPTY = { games: 0, best: 0, bestTime: 0, perfect: 0, lastScore: 0 };
const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Eski (tek setli) istatistik yapısını yeni set bazlı yapıya dönüştür
const normalizeStats = (stats) => {
  if (stats && typeof stats.games === "number") return { kb2: { ...EMPTY, ...stats }, egilim: { ...EMPTY } };
  return { kb2: { ...EMPTY, ...(stats?.kb2 || {}) }, egilim: { ...EMPTY, ...(stats?.egilim || {}) } };
};

export default function SkillMatch({ stats: rawStats, setStats: setRawStats }) {
  const allStats = useMemo(() => normalizeStats(rawStats), [rawStats]);
  const [setKey, setSetKey] = useState("kb2");
  const set = MATCH_SETS.find((s) => s.key === setKey) || MATCH_SETS[0];
  const stats = allStats[set.key];
  const setStats = (next) => setRawStats({ ...allStats, [set.key]: next });

  const [phase, setPhase] = useState("setup"); // setup | play | done
  const [level, setLevel] = useState(LEVELS[1]);
  const [rounds, setRounds] = useState([]);
  const [round, setRound] = useState(0);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [selL, setSelL] = useState(null);
  const [selR, setSelR] = useState(null);
  const [matched, setMatched] = useState({});
  const [wrong, setWrong] = useState(null);
  const [errors, setErrors] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(level.secs);
  const [lastWrongCodes, setLastWrongCodes] = useState({});
  const timerRef = useRef(null);
  const finishedRef = useRef(false);

  const total = set.items.length;
  const matchedCount = Object.keys(matched).length;

  const setupRound = (list, r) => {
    const items = list[r];
    setLeft(shuffle(items));
    setRight(shuffle(items));
    setSelL(null);
    setSelR(null);
  };

  const start = (lv) => {
    const chosen = lv || level;
    const groups = [];
    const all = shuffle(set.items);
    for (let i = 0; i < all.length; i += set.perRound) groups.push(all.slice(i, i + set.perRound));
    setLevel(chosen);
    setRounds(groups);
    setRound(0);
    setMatched({});
    setErrors(0);
    setStreak(0);
    setBestStreak(0);
    setLastWrongCodes({});
    setTimeLeft(chosen.secs);
    finishedRef.current = false;
    setupRound(groups, 0);
    setPhase("play");
  };

  useEffect(() => {
    if (phase !== "play") return undefined;
    timerRef.current = setInterval(() => setTimeLeft((t) => (t > 0 ? t - 1 : 0)), 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const finish = (completed) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    clearInterval(timerRef.current);
    const score = Math.max(0, matchedCount * 100 - errors * 15 + (completed ? timeLeft * 2 : 0));
    const timeUsed = level.secs - timeLeft;
    setStats({
      games: (stats.games || 0) + 1,
      best: Math.max(stats.best || 0, score),
      bestTime: completed ? (stats.bestTime ? Math.min(stats.bestTime, timeUsed) : timeUsed) : stats.bestTime || 0,
      perfect: (stats.perfect || 0) + (completed && errors === 0 ? 1 : 0),
      lastScore: score,
    });
    setPhase("done");
  };

  useEffect(() => {
    if (phase === "play" && timeLeft === 0) finish(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, phase]);

  useEffect(() => {
    if (phase !== "play" || !rounds.length) return undefined;
    const roundDone = rounds[round].every((s) => matched[s.code]);
    if (!roundDone) return undefined;
    if (matchedCount >= total) {
      const t = setTimeout(() => finish(true), 500);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setRound((r) => r + 1);
      setupRound(rounds, round + 1);
    }, 650);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matched, phase]);

  const tryMatch = (l, r) => {
    if (l.code === r.code) {
      setMatched((m) => ({ ...m, [l.code]: true }));
      setStreak((s) => { const n = s + 1; setBestStreak((b) => Math.max(b, n)); return n; });
      setSelL(null);
      setSelR(null);
    } else {
      setErrors((e) => e + 1);
      setStreak(0);
      setLastWrongCodes((w) => ({ ...w, [l.code]: true, [r.code]: true }));
      setWrong({ l: l.code, r: r.code });
      setTimeout(() => { setWrong(null); setSelL(null); setSelR(null); }, 520);
    }
  };

  const pickLeft = (s) => {
    if (matched[s.code] || wrong) return;
    if (selL?.code === s.code) return setSelL(null);
    if (selR) return tryMatch(s, selR);
    setSelL(s);
  };
  const pickRight = (s) => {
    if (matched[s.code] || wrong) return;
    if (selR?.code === s.code) return setSelR(null);
    if (selL) return tryMatch(selL, s);
    setSelR(s);
  };

  const timePct = (timeLeft / level.secs) * 100;
  const urgent = timeLeft <= 15;
  const review = useMemo(() => set.items.filter((s) => !matched[s.code] || lastWrongCodes[s.code]), [set, matched, lastWrongCodes]);
  const isEgilim = set.key === "egilim";
  const leftTitle = isEgilim ? "Eğilimler ve KB3 Becerileri" : "Beceriler";

  return (
    <div>
      <div className="section-head">
        <span className="eyebrow">KİMÖ-405 · Etkileşimli Araç</span>
        <h1 className="page-title">Beceri ve Eğilim Eşleştirme</h1>
        <p className="page-sub">Kavramları tanımlarıyla eşleştir. Sürükleme yok: önce kavrama, sonra tanıma dokun. İki set: 20 bütünleşik beceri (KB2) ve 21 eğilim + 3 üst düzey düşünme becerisi (E1/E2/E3 + KB3).</p>
      </div>

      {phase === "setup" && (
        <div data-testid="sm-setup">
          <div className="sm-sets" data-testid="sm-sets">
            {MATCH_SETS.map((s) => {
              const Icon = s.icon;
              const st = allStats[s.key];
              return (
                <button key={s.key} className={`sm-set ${set.key === s.key ? "active" : ""}`} onClick={() => setSetKey(s.key)} data-testid={`sm-set-${s.key}`}>
                  <span className="sm-set-icon"><Icon size={18} /></span>
                  <span className="sm-set-text">
                    <b>{s.label}</b>
                    <span>{s.sub}</span>
                  </span>
                  <span className="sm-set-best">{st.best ? <>{st.best} <small>rekor</small></> : <small>henüz oynanmadı</small>}</span>
                </button>
              );
            })}
          </div>

          <div className="sm-setup">
            <div className="sm-panel">
              <div className="panel-head"><Timer size={16} /> Zorluk seç</div>
              <div className="sm-levels">
                {LEVELS.map((lv) => (
                  <button key={lv.key} className={`sm-level ${level.key === lv.key ? "active" : ""}`} onClick={() => setLevel(lv)} data-testid={`sm-level-${lv.key}`}>
                    <b>{lv.label}</b><span>{lv.desc}</span>
                  </button>
                ))}
              </div>
              <ul className="sm-rules">
                <li>{set.perRound}'{set.perRound === 6 ? "şar" : "er"}li <b>{Math.ceil(total / set.perRound)} tur</b>, toplam <b>{total} eşleştirme</b></li>
                <li>Doğru eşleşme <b>+100</b>, yanlış deneme <b>−15</b> puan</li>
                <li>Hepsini bitirirsen kalan her saniye <b>+2</b> puan</li>
                {isEgilim && <li>Eşleşince kavramın kategorisi (E1 / E2 / E3 / KB3) görünür — sınıflandırmayı da pekiştirirsin</li>}
              </ul>
              <button className="btn btn-accent sm-start" onClick={() => start()} data-testid="sm-start"><Play size={16} /> {set.label} oyununu başlat</button>
            </div>
            <div className="sm-panel sm-best">
              <div className="panel-head"><Trophy size={16} /> Rekorların · {set.label}</div>
              <div className="sm-best-grid">
                <div><b>{stats.best || 0}</b><span>En yüksek puan</span></div>
                <div><b>{stats.bestTime ? fmtTime(stats.bestTime) : "–"}</b><span>En hızlı tam tur</span></div>
                <div><b>{stats.games || 0}</b><span>Oynanan oyun</span></div>
                <div><b>{stats.perfect || 0}</b><span>Hatasız bitiriş</span></div>
              </div>
              {(stats.games || 0) > 0 && (
                <button className="btn btn-ghost" onClick={() => setStats({ ...EMPTY })} data-testid="sm-reset-stats"><RotateCcw size={14} /> Bu setin rekorlarını sıfırla</button>
              )}
            </div>
          </div>
        </div>
      )}

      {phase === "play" && (
        <div data-testid="sm-board">
          <div className={`sm-hud ${urgent ? "urgent" : ""}`}>
            <div className="sm-hud-row">
              <div className="sm-time" data-testid="sm-timer"><Timer size={18} /> {fmtTime(timeLeft)}</div>
              <div className="sm-hud-stats">
                <span className="sm-hud-set">{set.label}</span>
                <span data-testid="sm-round">Tur <b>{round + 1}/{rounds.length}</b></span>
                <span data-testid="sm-matched"><CheckCircle2 size={14} className="ok" /> <b>{matchedCount}</b>/{total}</span>
                <span data-testid="sm-errors"><XCircle size={14} className="bad" /> <b>{errors}</b></span>
                <span><Flame size={14} className="warn-ic" /> <b>{streak}</b></span>
              </div>
              <button className="btn btn-ghost sm-quit" onClick={() => finish(false)} data-testid="sm-quit">Bitir</button>
            </div>
            <div className="sm-timebar"><div style={{ width: `${timePct}%` }} /></div>
          </div>

          <div className="sm-help">{selL ? <>Şimdi <b>{selL.name}</b> için doğru tanıma dokun</> : selR ? <>Bu tanıma uyan <b>kavrama</b> dokun</> : "Bir kavrama ya da tanıma dokunarak başla"}</div>

          <div className="sm-grid">
            <div className="sm-col">
              <div className="sm-col-title">{leftTitle}</div>
              <div className="sm-chips">
                {left.map((s) => {
                  let cls = "sm-chip";
                  if (matched[s.code]) cls += " done";
                  else if (wrong?.l === s.code) cls += " wrong";
                  else if (selL?.code === s.code) cls += " sel";
                  return (
                    <button key={s.code} className={cls} onClick={() => pickLeft(s)} disabled={!!matched[s.code]} data-testid={`sm-skill-${s.code}`}>
                      {matched[s.code] && <CheckCircle2 size={14} />} {s.name}
                      {matched[s.code] && isEgilim && <span className={`sm-cat cat-${s.cat}`}>{s.cat}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="sm-col">
              <div className="sm-col-title">Tanımlar</div>
              <div className="sm-defs">
                {right.map((s) => {
                  let cls = "sm-def";
                  if (matched[s.code]) cls += " done";
                  else if (wrong?.r === s.code) cls += " wrong";
                  else if (selR?.code === s.code) cls += " sel";
                  return (
                    <button key={s.code} className={cls} onClick={() => pickRight(s)} disabled={!!matched[s.code]} data-testid={`sm-def-${s.code}`}>
                      {matched[s.code] && <span className="sm-def-tag">{s.code} · {s.name}{isEgilim ? ` · ${s.catLabel}` : ""}</span>}
                      <span>{s.def}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {phase === "done" && (
        <div data-testid="sm-result">
          <div className={`result-card ${matchedCount === total ? "" : "result-partial"}`}>
            <Trophy size={34} />
            <div>
              <h3>{matchedCount === total ? (errors === 0 ? "Kusursuz! Tüm kavramlar hatasız eşleşti." : `Tebrikler, ${total} kavramın tamamını eşleştirdin!`) : "Süre doldu."}</h3>
              <p>
                {set.label} · Puan: <b data-testid="sm-score">{stats.lastScore || 0}</b> · Eşleşen {matchedCount}/{total} · Yanlış deneme {errors} · En uzun seri {bestStreak}
                {matchedCount === total && <> · Süre {fmtTime(level.secs - timeLeft)}</>}
                {stats.best === stats.lastScore && stats.lastScore > 0 && <> · <b>Yeni rekor!</b></>}
              </p>
            </div>
            <div className="sm-result-actions">
              <button className="btn btn-accent" onClick={() => start()} data-testid="sm-again"><RotateCcw size={15} /> Tekrar oyna</button>
              <button className="btn btn-ghost" onClick={() => setPhase("setup")} data-testid="sm-setup-btn">Set / zorluk değiştir</button>
            </div>
          </div>

          {review.length > 0 ? (
            <div className="sm-review">
              <div className="sm-col-title">Gözden geçir: {review.length} kavram ({matchedCount === total ? "yanlış denediğin" : "eşleşmeyen veya yanlış denediğin"})</div>
              <div className="skills-grid">
                {review.map((s) => (
                  <div key={s.code} className="skill-item">
                    <div className="skill-code">{s.code}</div>
                    <div>
                      <div className="skill-name">{s.name}{s.cat === "KB2" ? " Becerisi" : ""} <span className="muted">· {s.catLabel}</span></div>
                      <div className="skill-def">{s.def}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty small"><Puzzle size={22} /><p>Hiç yanlış denemen yok. Diğer setle devam edebilirsin <ArrowRight size={14} style={{ display: "inline" }} /></p></div>
          )}
        </div>
      )}
    </div>
  );
}
